import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { renovacaoRuleService } from '@/services/renovacaoRuleService';
import type { RenovacaoStatus, RenovacaoHistoryEntry } from '@/types/renovacao';
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react';

interface Props {
  clientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_BADGE: Record<RenovacaoStatus, { label: string; className: string }> = {
  SENT: { label: 'Enviado', className: 'bg-emerald-100 text-emerald-700' },
  FAILED: { label: 'Falha', className: 'bg-red-100 text-red-700' },
  IGNORED: { label: 'Ignorado', className: 'bg-gray-100 text-gray-700' },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

const LIMIT = 50;

export const RenovacaoHistoryDialog: React.FC<Props> = ({ clientId, open, onOpenChange }) => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<RenovacaoStatus | 'ALL'>('ALL');
  const [data, setData] = useState<RenovacaoHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await renovacaoRuleService.listHistory(clientId, {
        page,
        limit: LIMIT,
        status: status === 'ALL' ? undefined : status,
      });
      setData(r.data || []);
      setTotal(r.total || 0);
    } catch (e) {
      console.error('[Renovacao] erro ao carregar histórico completo:', e);
    } finally {
      setLoading(false);
    }
  }, [clientId, page, status]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Histórico completo</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-4">
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v as RenovacaoStatus | 'ALL');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="SENT">Enviados</SelectItem>
              <SelectItem value="FAILED">Falhas</SelectItem>
              <SelectItem value="IGNORED">Ignorados</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500">Total: {total}</span>
          {loading && <span className="text-xs text-gray-400">Carregando…</span>}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500">
              <tr>
                <th className="py-2">Data/hora</th>
                <th>Associado</th>
                <th>Telefone</th>
                <th>Veículo</th>
                <th>Status</th>
                <th>Situação</th>
                <th>Erro</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const badge = STATUS_BADGE[row.status];
                return (
                  <tr key={row.id} className="border-t">
                    <td className="py-2">{formatDateTime(row.executed_at)}</td>
                    <td>
                      {row.associado_nome || '—'}{' '}
                      <span className="text-gray-400">({row.codigo_associado})</span>
                    </td>
                    <td>{row.telefone || '—'}</td>
                    <td>
                      {row.placa || '—'}{' '}
                      <span className="text-gray-400">({row.codigo_veiculo ?? '—'})</span>
                    </td>
                    <td><Badge className={badge.className}>{badge.label}</Badge></td>
                    <td>
                      {row.status === 'SENT' &&
                        (row.situacao_atualizada ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <X className="w-4 h-4 text-amber-600" />
                        ))}
                    </td>
                    <td className="max-w-xs truncate" title={row.error_message || ''}>
                      {row.error_message || ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </Button>
          <span className="text-sm">Página {page} de {totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RenovacaoHistoryDialog;
